import React, { useState, useEffect, useRef } from "react";
import SecondaryButton from "../buttons/SecondaryButton";
import Icon1 from "@assets/icons/Icon";
import CheckBox from "../selection/CheckBox";
import { ChunkyTabs } from "../selection/ChunkyTabs";

// Add column configuration button
type ColumnType = {
  field: string;
  headerName: string;
  width?: number;
  flex?: number;
  type?: string;
  renderCell?: (params: {
    value: any;
    row: any;
    field: string;
    colDef: ColumnType;
  }) => React.ReactNode;
};

interface DataGridProps {
  table: { columns: ColumnType[]; rows: any[] };
  show_ranking?: boolean;
  onClick?: (row: any) => void;
  is_folders?: boolean;
  initial_sort_field?: string;
  initial_asc?: string;
  handleSortChange?: (sortModel: { field: string; sort: string }[]) => void;
  handleSelectionModelChange?: (selectionModel: any[]) => void;
  initial_saved_columns_sizes?: { [key: string]: number };
  initial_saved_columns_showed?: { [key: string]: boolean };
  initialPage?: number;
  initialPageSize?: number;
  setPaginationModel?: (model: any) => void;
  handleColumnManagementChange?: (model: any) => void;
  handleColumnSizeChange?: (params: {
    colDef: { width: number; field: string };
  }) => void;
  selectable?: boolean;
  orgData?: any;
  handleResetConfig?: () => void;
  hide_lines?: boolean;
  tabs?: { value: string; label: string }[];
  activeTab?: string;
  onTabChange?: (tab: string) => void;
}

const DataGrid = ({
  table,
  show_ranking = false,
  onClick = () => {},
  is_folders = false,
  initial_sort_field = "id",
  initial_asc = "asc",
  handleSortChange = () => {},
  handleSelectionModelChange = () => {},
  initial_saved_columns_sizes = {},
  hide_lines = false,
  initial_saved_columns_showed = is_folders
    ? {
        id: false,
        num: false,
        state: false,
      }
    : { id: false }, // Set the default initial column visibility
  initialPage = 0,
  initialPageSize = 50,
  setPaginationModel = () => {},
  handleColumnManagementChange = () => {},
  handleColumnSizeChange = () => {},
  selectable = true,
  orgData,
  handleResetConfig = () => {},
  tabs,
  activeTab: activeTabProp,
  onTabChange,
}: DataGridProps) => {
  // Pagination state

  const { columns, rows } = table;
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [rowSelectionModel, onRowSelectionModelChange] = useState<any>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  // Column configuration dropdown state
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const columnConfigRef = useRef(null);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: initial_sort_field,
    direction: initial_asc,
  });

  // Column visibility state
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    is_folders
      ? {
          ...initial_saved_columns_showed,
          state: searchValue === "" ? false : true,
        }
      : initial_saved_columns_showed,
  );

  // Column sizes state
  const [columnSizes, setColumnSizes] = useState(initial_saved_columns_sizes);

  // Initialize flag
  const [initialised, setInitialised] = useState(false);

  // References to prevent infinite loops
  const isFirstRender = useRef(true);
  const prevSearchValue = useRef(searchValue);

  const scrollRef = useRef<any>(null);

  const resetScroll = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  // Selection state

  // Handle click outside column config dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        columnConfigRef.current &&
        !(columnConfigRef.current as HTMLElement | null)?.contains(event.target)
      ) {
        setColumnConfigOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Initialize component
  useEffect(() => {
    setInitialised(true);
  }, []);

  // Effect to update pagination model when page or size changes

  // Effect to set initial column visibility
  useEffect(() => {
    if (prevSearchValue.current !== searchValue) {
      prevSearchValue.current = searchValue;

      const new_column_visibility_model = { ...columnVisibilityModel };

      if (is_folders) {
        if (searchValue !== "") {
          new_column_visibility_model["state"] = true;
        } else {
          new_column_visibility_model["state"] = false;
        }
      }

      setColumnVisibilityModel(new_column_visibility_model);

      // Use a timeout to prevent circular updates
      const timeoutId = setTimeout(() => {
        handleColumnManagementChange(new_column_visibility_model);
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [
    searchValue,
    is_folders,
    columnVisibilityModel,
    handleColumnManagementChange,
  ]);

  // Pagination logic
  const totalPages = Math.ceil(rows.length / pageSize);

  function lowerAndTrim(str: any, key: any = null) {
    if (key === "all_tags") {
      let tags = str?.filter((item: any) => item?.org_ref.id === orgData?.id);
      let tag_str = tags?.map((item: any) => item?.nom);
      //return str?.map((item) => item.toLowerCase().trim());
      return tag_str;
    }
    if (typeof str !== "string") {
      return str;
    }
    return str?.toLowerCase()?.trim();
  }

  // Sorting logic
  const sortedRows = React.useMemo(() => {
    const sortableRows = [...rows];
    if (sortConfig.key !== null) {
      sortableRows.sort((a, b) => {
        if (
          lowerAndTrim(a[sortConfig.key], sortConfig.key) <
          lowerAndTrim(b[sortConfig.key], sortConfig.key)
        ) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (
          lowerAndTrim(a[sortConfig.key], sortConfig.key) >
          lowerAndTrim(b[sortConfig.key], sortConfig.key)
        ) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableRows;
  }, [rows, sortConfig]);
  const paginatedRows = sortedRows.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize,
  );

  // Handler for sort request
  const requestSort = (key: any) => {
    if (!initialised) {
      setInitialised(true);
    }

    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });

    // Call external sort handler
    handleSortChange(key && direction ? [{ field: key, sort: direction }] : []);
  };

  // Toggle a single column's visibility
  const toggleColumnVisibility = (field: any) => {
    const new_model = { ...columnVisibilityModel };
    new_model[field] = !new_model[field];

    setColumnVisibilityModel(new_model);

    // Use a timeout to prevent circular updates
    setTimeout(() => {
      handleColumnManagementChange(new_model);
    }, 0);
  };

  // Handler for column resize
  const handleColumnResize = (field: any, newWidth: any) => {
    const newSizes = {
      ...columnSizes,
      [field]: newWidth,
    };
    setColumnSizes(newSizes);

    // Use a timeout to prevent potential circular updates
    handleColumnSizeChange({
      colDef: {
        width: newWidth,
        field: field,
      },
    });
  };

  // Row selection handler
  const handleRowSelectionChange = (id: any) => {
    let newSelection;

    if (rowSelectionModel.includes(id)) {
      // If row is already selected, remove it
      newSelection = rowSelectionModel.filter((rowId: any) => rowId !== id);
    } else {
      // If row is not selected, add it to selection
      newSelection = [...rowSelectionModel, id];
    }

    onRowSelectionModelChange(newSelection);
    handleSelectionModelChange(newSelection);
  };

  // Handler for row click
  const handleRowClick = (row: any) => {
    onClick(row);
  };

  // Function to calculate column width based on width or flex
  const getColumnWidth = (column: any) => {
    // Use custom saved width if available
    if (columnSizes[column.field] !== undefined) {
      return `${columnSizes[column.field]}px`;
    }
    // Use explicit width if provided
    if (column.width) {
      return `${column.width}px`;
    }

    // Use flex proportional width if provided
    if (column.flex) {
      // For flex, we'll convert it to a percentage of the table width
      // This is a simplified approach - actual flex would need container refs

      if ((column.flex * 100) / totalFlexUnits < (column.minWidth || 100)) {
        return `${column.minWidth || 100}px`;
      } else {
        return `${(column.flex * 100) / totalFlexUnits}%`;
      }
    }

    // Default width if nothing specified
    return "auto";
  };

  // Calculate total flex units once
  const totalFlexUnits = React.useMemo(() => {
    return columns.reduce((total, column) => total + (column.flex || 0), 0);
  }, [columns]);

  // Helper function to render cell content
  const renderCellContent = (column: any, row: any) => {
    // If the column has a renderCell function, use it
    if (column.renderCell) {
      // Create params object similar to MUI DataGrid
      const params = {
        value: row[column.field],
        row: row,
        field: column.field,
        colDef: column,
      };
      return column.renderCell(params);
    }

    const value = row[column.field];

    // Handle date type columns
    if (column.type === "date" && value instanceof Date) {
      return value.toLocaleDateString();
    }

    // Handle date strings that need to be converted
    if (column.type === "date" && typeof value === "string") {
      try {
        const date = new Date(value);
        return date.toLocaleDateString();
      } catch (e) {
        return value; // Return original value if date conversion fails
      }
    }

    // Don't render objects
    if (
      typeof value === "object" &&
      value !== null &&
      !(value instanceof Date)
    ) {
      return "[Object]";
    }

    // Otherwise, just return the cell value
    return value;
  };

  useEffect(() => {
    setCurrentPage(0);
  }, [rows]);

  // Get visible columns
  const visibleColumns = React.useMemo(() => {
    return columns.filter((col) => columnVisibilityModel[col.field] !== false);
  }, [columns, columnVisibilityModel]);

  return (
    <div className="w-full h-full  border max-w-full border-neutral-200 rounded-xl  shadow flex flex-col">
      <div className="w-full overflow-y-auto overflow-x-auto ">
        <div className="w-full ">
          <div
            style={{ zIndex: 1 }}
            className="w-full bg-neutral-50 rounded-xl sticky  top-0 "
          >
            {tabs && activeTabProp && onTabChange && (
              <div className="p-2 w-fit">
                <ChunkyTabs
                  tabs={tabs}
                  activeTab={activeTabProp}
                  setActiveTab={(tab) => onTabChange(tab)}
                />
              </div>
            )}
            <table
              className="w-full border-collapse"
              style={{ tableLayout: "fixed", borderSpacing: 0 }}
            >
              <colgroup>
                {selectable && (
                  <col
                    style={{
                      width: "55px",
                    }}
                  />
                )}

                {show_ranking && (
                  <col
                    style={{
                      width: "40px",
                    }}
                  />
                )}

                {visibleColumns.map((column) => (
                  <col
                    key={`col-${column.field}`}
                    style={{ width: getColumnWidth(column) }}
                  />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {selectable && (
                    <th
                      style={{
                        paddingLeft: 15,
                        overflow: "hidden",
                        boxSizing: "border-box",
                        left: 0,

                        zIndex: 10,
                      }}
                    >
                      <CheckBox
                        checked={
                          rows.length > 0 &&
                          rowSelectionModel.length === rows.length
                        }
                        onChange={(checked) => {
                          if (checked) {
                            const allIds = rows.map((row) => row.id);

                            onRowSelectionModelChange(allIds);
                            handleSelectionModelChange(allIds);
                          } else {
                            onRowSelectionModelChange([]);
                            handleSelectionModelChange([]);
                          }
                        }}
                      ></CheckBox>
                    </th>
                  )}
                  {show_ranking && (
                    <col
                      style={{
                        width: "40px",
                        maxWidth: "40px",
                        minWidth: "40px",
                      }}
                    />
                  )}
                  {visibleColumns.map((column) => (
                    <th
                      key={column.field}
                      className={`
                        ${
                          sortConfig.key === column.field
                            ? "border-b-2 border-neutral-500"
                            : ""
                        }
                        
                        text-left pr-4 text-neutral-500 p-2 font-medium text-h3 uppercase w-fit relative cursor-pointer whitespace-nowrap select-none`}
                      onClick={() => requestSort(column.field)}
                    >
                      <div className="flex items-center space-x-1">
                        <span className="text-ellipsis text-h5 text-neutral-400 overflow-hidden whitespace-nowrap">
                          {column.headerName}
                        </span>
                        {sortConfig.key === column.field && (
                          <span className="pl-0 rounded-full">
                            {sortConfig.direction === "asc" ? (
                              <Icon1 name="up2" size={15} color="black"></Icon1>
                            ) : (
                              <Icon1
                                name="down2"
                                size={15}
                                color="black"
                              ></Icon1>
                            )}
                          </span>
                        )}
                      </div>
                      <div
                        className="absolute right-0 top-0 h-full w-1 rounded-lg bg-neutral-100 cursor-col-resize hover:bg-neutral-300"
                        onMouseDown={(e) => {
                          const startX = e.pageX;
                          const startWidth =
                            columnSizes[column.field] || column.width || 100;

                          const onMouseMove = (moveEvent: any) => {
                            const newWidth = Math.max(
                              80,
                              startWidth + (moveEvent.pageX - startX),
                            );
                            handleColumnResize(column.field, newWidth);
                          };

                          const onMouseUp = () => {
                            document.removeEventListener(
                              "mousemove",
                              onMouseMove,
                            );
                            document.removeEventListener("mouseup", onMouseUp);
                          };

                          document.addEventListener("mousemove", onMouseMove);
                          document.addEventListener("mouseup", onMouseUp);
                        }}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
          </div>
          <div
            ref={scrollRef}
            className="w-full overflow-auto-y "
            style={{ maxHeight: "calc(100vh - 200px)" }}
          >
            <table
              className="w-full border-collapse"
              style={{ tableLayout: "fixed", borderSpacing: 0 }}
            >
              <colgroup>
                {selectable && (
                  <col
                    style={{
                      width: "55px",
                    }}
                  />
                )}
                {show_ranking && (
                  <col
                    style={{
                      width: "40px",
                    }}
                  />
                )}

                {visibleColumns.map((column) => (
                  <col
                    key={`col-body-${column.field}`}
                    style={{ width: getColumnWidth(column) }}
                  />
                ))}
              </colgroup>
              <tbody
                className={`
                ${hide_lines ? "" : "divide-y divide-neutral-200"}
                bg-white `}
              >
                {paginatedRows.map((row, rowIndex) => (
                  <tr
                    key={row.id || rowIndex}
                    className={`hover:bg-neutral-50 cursor-pointer ${
                      rowSelectionModel.includes(row.id) ? "bg-neutral-50" : ""
                    }`}
                    onClick={() => handleRowClick(row)}
                  >
                    {selectable && (
                      <td
                        style={{
                          paddingLeft: 15,
                          overflow: "hidden",
                          boxSizing: "border-box",
                          zIndex: -1,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <CheckBox
                          checked={rowSelectionModel.includes(row.id)}
                          onChange={() => {
                            handleRowSelectionChange(row.id);
                          }}
                        ></CheckBox>
                      </td>
                    )}
                    {show_ranking && (
                      <td
                        style={{
                          width: "40px",
                          minWidth: "40px",
                          maxWidth: "40px",
                        }}
                        className="text-h3 text-shadow_50"
                      >
                        {rowIndex + 1}
                      </td>
                    )}
                    {visibleColumns.map((column) => (
                      <td
                        key={`${row.id || rowIndex}-${column.field}`}
                        style={{
                          padding: "8px 3px",

                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          boxSizing: "border-box",
                        }}
                        className="text-h2 text-neutral-500"
                      >
                        {renderCellContent(column, row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-white px-4 py-2 flex items-center rounded-xl h-fit justify-between  sm:px-2 mt-auto">
        <div className="flex-1 flex h-fit justify-between sm:hidden">
          <button
            onClick={() => {
              const newPage = Math.max(0, currentPage - 1);
              setCurrentPage(newPage);
            }}
            disabled={currentPage === 0}
            className="relative inline-flex items-center px-4 py-1 border border-neutral-300 text-sm font-medium rounded-md text-neutral-550 bg-white hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Précédent
          </button>
          <button
            onClick={() => {
              const newPage = Math.min(totalPages - 1, currentPage + 1);
              setCurrentPage(newPage);
            }}
            disabled={currentPage === totalPages - 1}
            className="ml-3 relative inline-flex items-center px-4 py-1 border border-neutral-300 text-sm font-medium rounded-md text-neutral-550 bg-white hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Suivant
          </button>
        </div>

        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div className="flex flex-row items-center gap-3 w-full">
            <div className="flex items-center flex-row border  px-2 py-1 rounded-md  gap-1 text-h2">
              {/* Page size selector */}
              Affichés
              <select
                value={pageSize}
                onChange={(e) => {
                  const newSize = Number(e.target.value);
                  setPageSize(newSize);
                  setCurrentPage(0); // Reset to first page on page size change
                  resetScroll();
                }}
                className=" bg-neutral-100 font-medium mx-1 hover:ring-2 hover:ring-neutral-300  cursor-pointer rounded-md text-sm py-0  px-1"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
              </select>
              sur
              <span className="font-medium"> {rows.length}</span>
            </div>{" "}
            <div className="flex  pr-2 mr-auto">
              <div className="relative" ref={columnConfigRef}>
                <button
                  onClick={() => setColumnConfigOpen(!columnConfigOpen)}
                  className="py-1 px-3 border  rounded-md text-sm font-medium text-neutral-550 bg-white hover:bg-neutral-50 flex items-center gap-2"
                >
                  <span>Colonnes</span>
                  <Icon1 name="settings" size={18} color="black"></Icon1>
                </button>

                {columnConfigOpen && (
                  <div className="absolute bottom-8 z-10 mt-1 w-56 bg-white border border-neutral-200 rounded-md shadow-lg py-1">
                    <div className="px-3 py-1  font-medium text-h2 text-neutral-550">
                      Colonnes visibles
                    </div>
                    <div className="px-3 py-2 border-b border-neutral-200 font-medium text-neutral-550">
                      <SecondaryButton
                        onClick={() => {
                          handleResetConfig();
                        }}
                        variant="small"
                      >
                        Réinitialiser
                      </SecondaryButton>
                    </div>
                    <div className="max-h-200 overflow-y-auto py-1">
                      {columns.map((column) => (
                        <div
                          key={column.field}
                          className="px-3 py-1 hover:bg-neutral-100 flex flex-row gap-2 items-center"
                        >
                          <CheckBox
                            className="cursor-pointer"
                            checked={
                              columnVisibilityModel[column.field] !== false
                            }
                            onChange={() =>
                              toggleColumnVisibility(column.field)
                            }
                          />
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <span className="text-sm">{column.headerName}</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <nav
              className="relative z-0 inline-flex h-8 ml-auto rounded-md shadow-sm -space-x-px"
              aria-label="Pagination"
            >
              <button
                onClick={() => {
                  const newPage = Math.max(0, currentPage - 1);
                  setCurrentPage(newPage);
                  resetScroll();
                }}
                disabled={currentPage === 0}
                className="relative inline-flex items-center px-2 py-1 rounded-l-md border border-neutral-300 bg-white text-sm font-medium text-neutral-500 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              {(() => {
                const pageButtons = [];

                // Always show first page
                if (currentPage > 2) {
                  pageButtons.push(
                    <button
                      key={0}
                      onClick={() => {
                        setCurrentPage(0);
                        resetScroll();
                      }}
                      className="bg-white border-neutral-300 text-neutral-500 hover:bg-neutral-50 relative inline-flex items-center px-4 py-1 border text-sm font-medium"
                    >
                      1
                    </button>,
                  );

                  // Add ellipsis if needed
                  if (currentPage > 3) {
                    pageButtons.push(
                      <span
                        key="ellipsis1"
                        className="relative inline-flex items-center px-4 py-1 border border-neutral-300 bg-white text-sm font-medium text-neutral-550"
                      >
                        ...
                      </span>,
                    );
                  }
                }

                // Calculate range of pages to show
                const startPage = Math.max(0, currentPage - 1);
                const endPage = Math.min(totalPages - 1, currentPage + 1);

                // Add current page range
                for (let i = startPage; i <= endPage; i++) {
                  pageButtons.push(
                    <button
                      key={i}
                      onClick={() => {
                        setCurrentPage(i);
                        resetScroll();
                      }}
                      className={`${
                        currentPage === i
                          ? "bg-neutral-50 border-neutral-500 text-neutral-600"
                          : "bg-white border-neutral-300 text-neutral-500 hover:bg-neutral-50"
                      } relative inline-flex items-center px-4 py-1 border text-sm font-medium`}
                    >
                      {i + 1}
                    </button>,
                  );
                }

                // Add ellipsis and last page if needed
                if (currentPage < totalPages - 3) {
                  pageButtons.push(
                    <span
                      key="ellipsis2"
                      className="relative inline-flex items-center px-4 py-1 border border-neutral-300 bg-white text-sm font-medium text-neutral-550"
                    >
                      ...
                    </span>,
                  );
                }

                if (currentPage < totalPages - 2 && totalPages > 1) {
                  pageButtons.push(
                    <button
                      key={totalPages - 1}
                      onClick={() => {
                        setCurrentPage(totalPages - 1);
                        resetScroll();
                      }}
                      className="bg-white border-neutral-300 text-neutral-500 hover:bg-neutral-50 relative inline-flex items-center px-4 py-1 border text-sm font-medium"
                    >
                      {totalPages}
                    </button>,
                  );
                }

                return pageButtons;
              })()}
              <button
                onClick={() => {
                  const newPage = Math.min(totalPages - 1, currentPage + 1);
                  setCurrentPage(newPage);
                  resetScroll();
                }}
                disabled={currentPage === totalPages - 1 || totalPages === 0}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-neutral-300 bg-white text-sm font-medium text-neutral-500 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataGrid;
